// Minimal markdown renderer for streamed assistant text. Supports exactly what
// the assistant emits — paragraphs, bold, italic, inline code, and bullet/
// ordered lists — with zero dependencies. We build React nodes directly (never
// dangerouslySetInnerHTML), so streamed, partially-formed markdown is always
// safe to render mid-token.

import { Fragment, type ReactNode } from 'react';

/** Parse inline `**bold**`, `*italic*` / `_italic_`, and `` `code` ``. */
export function renderInline(text: string, keyPrefix = 'i'): ReactNode[] {
  const nodes: ReactNode[] = [];
  // One pass over a combined token regex keeps ordering correct and handles the
  // half-open tokens that streaming produces (an unmatched `**` renders as text).
  const re = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\*([^*]+)\*)|(_([^_]+)_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${n++}`;
    if (m[1] !== undefined) nodes.push(<strong key={key}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<code key={key}>{m[4]}</code>);
    else if (m[5] !== undefined) nodes.push(<em key={key}>{m[6]}</em>);
    else if (m[7] !== undefined) nodes.push(<em key={key}>{m[8]}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

type Block =
  | { type: 'p'; lines: string[] }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] };

const BULLET = /^\s*[-*]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;

/** Group lines into paragraph / list blocks. */
export function parseBlocks(src: string): Block[] {
  const blocks: Block[] = [];
  const lines = src.replace(/\r\n/g, '\n').split('\n');

  for (const line of lines) {
    const bullet = BULLET.exec(line);
    const ordered = !bullet ? ORDERED.exec(line) : null;
    const last = blocks[blocks.length - 1];

    if (bullet) {
      const item = bullet[1] ?? '';
      if (last?.type === 'ul') last.items.push(item);
      else blocks.push({ type: 'ul', items: [item] });
    } else if (ordered) {
      const item = ordered[1] ?? '';
      if (last?.type === 'ol') last.items.push(item);
      else blocks.push({ type: 'ol', items: [item] });
    } else if (line.trim() === '') {
      // Blank line separates paragraphs: force the next text line to open a
      // fresh block (empty placeholders are dropped by the filter below).
      if (last?.type === 'p' && last.lines.length > 0) blocks.push({ type: 'p', lines: [] });
    } else {
      if (last?.type === 'p') last.lines.push(line);
      else blocks.push({ type: 'p', lines: [line] });
    }
  }
  // Drop empty paragraph placeholders left by blank-line handling.
  return blocks.filter((b) => (b.type === 'p' ? b.lines.length > 0 : b.items.length > 0));
}

export function MarkdownLite({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'ul')
          return (
            <ul key={i}>
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, `${i}-${j}`)}</li>
              ))}
            </ul>
          );
        if (b.type === 'ol')
          return (
            <ol key={i}>
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, `${i}-${j}`)}</li>
              ))}
            </ol>
          );
        return (
          <p key={i}>
            {b.lines.map((ln, j) => (
              <Fragment key={j}>
                {j > 0 && <br />}
                {renderInline(ln, `${i}-${j}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}
